/**
 * AttendanceFilterBar.jsx — Dropdown filtre çubuğu
 * Tarih, Departman, Mesai Planı, Durum, Onay dropdown'ları
 */
import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Filter,
  Search,
  CalendarDays,
  Building2,
  Clock,
  CheckCircle,
  FileSpreadsheet,
  X,
  RotateCcw,
} from 'lucide-react';

const DropdownMenu = ({ isOpen, onClose, children, alignRight = false, className = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className={`absolute z-50 mt-1.5 min-w-[200px] rounded-2xl border border-slate-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150 ${alignRight ? 'right-0' : 'left-0'} ${className}`}
    >
      {children}
    </div>
  );
};

const DropdownButton = ({ label, icon: Icon, isActive, onClick, activeFilterCount = 0 }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition-all
      ${isActive
        ? 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200'
        : activeFilterCount > 0
          ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
          : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
      }
    `}
  >
    {Icon && <Icon size={14} />}
    {label}
    {activeFilterCount > 0 && (
      <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-black text-white">
        {activeFilterCount}
      </span>
    )}
  </button>
);

const AttendanceFilterBar = ({
  filters,
  draft,
  activeFilterMenu,
  hasActiveFilter,
  openFilterMenu,
  applyFilters,
  resetFilters,
  setDraftField,
  departments = [],
  schedules = [],
  onExport,
  showExport = true,
}) => {
  const { t } = useTranslation();

  const filterMenus = [
    {
      key: 'date',
      label: t('attendance_filter_date', 'Tarih'),
      icon: CalendarDays,
      content: (
        <div className="space-y-2.5 p-1">
          <label className="block">
            <span className="text-[10px] font-bold text-slate-500 uppercase">{t('attendance_filter_from', 'Başlangıç')}</span>
            <input
              type="date"
              value={draft.from_date || ''}
              onChange={(e) => setDraftField('from_date', e.target.value)}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold text-slate-500 uppercase">{t('attendance_filter_to', 'Bitiş')}</span>
            <input
              type="date"
              value={draft.to_date || ''}
              onChange={(e) => setDraftField('to_date', e.target.value)}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium"
            />
          </label>
        </div>
      ),
    },
    {
      key: 'personnel',
      label: t('attendance_filter_personnel', 'Personel'),
      icon: Search,
      content: (
        <div className="space-y-2.5 p-1">
          <label className="block">
            <span className="text-[10px] font-bold text-slate-500 uppercase">{t('attendance_filter_search', 'Ad Soyad Ara')}</span>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={draft.query || ''}
                onChange={(e) => setDraftField('query', e.target.value)}
                placeholder={t('attendance_filter_search_placeholder', 'İsim veya soyisim...')}
                className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-xs font-medium"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] font-bold text-slate-500 uppercase">{t('attendance_filter_department', 'Departman')}</span>
            <select
              value={draft.department || 'ALL'}
              onChange={(e) => setDraftField('department', e.target.value)}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium"
            >
              <option value="ALL">{t('attendance_filter_all_departments', 'Tüm Departmanlar')}</option>
              {(departments || []).map((d) => (
                <option key={d.id} value={d.id}>{d.name || d.department_name}</option>
              ))}
            </select>
          </label>
        </div>
      ),
    },
    {
      key: 'status',
      label: t('attendance_filter_status', 'Durum'),
      icon: CheckCircle,
      content: (
        <div className="space-y-1.5 p-1">
          {[
            { value: 'ALL', label: t('attendance_filter_all_status', 'Tüm Durumlar') },
            { value: 'PRESENT', label: t('status_present', 'Mevcut') },
            { value: 'LATE', label: t('status_late', 'Geç Kaldı') },
            { value: 'ABSENT', label: t('status_absent', 'Devamsız') },
            { value: 'SICK_REPORT', label: t('status_sick_report', 'Sağlık Raporu') },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDraftField('status', opt.value)}
              className={`w-full text-left rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                draft.status === opt.value
                  ? 'bg-cyan-50 text-cyan-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ),
    },
    {
      key: 'approval',
      label: t('attendance_filter_approval', 'Onay'),
      icon: CheckCircle,
      content: (
        <div className="space-y-1.5 p-1">
          {[
            { value: 'ALL', label: t('attendance_filter_all_approval', 'Tüm Onaylar') },
            { value: 'PENDING', label: t('badge_manager_pending', 'Müdür Onayı Bekliyor') },
            { value: 'MANAGER_APPROVED', label: t('badge_hr_pending', 'İK Onayı Bekliyor') },
            { value: 'HR_APPROVED', label: t('badge_payroll_ready', 'Bordroya Hazır') },
            { value: 'REJECTED', label: t('badge_rejected', 'Reddedildi') },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDraftField('approval', opt.value)}
              className={`w-full text-left rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                draft.approval === opt.value
                  ? 'bg-cyan-50 text-cyan-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex flex-wrap items-center gap-1.5">
        {/* Dropdown butonları */}
        {filterMenus.map((menu) => (
          <div key={menu.key} className="relative">
            <DropdownButton
              label={menu.label}
              icon={menu.icon}
              isActive={activeFilterMenu === menu.key}
              activeFilterCount={hasActiveFilter(menu.key) ? 1 : 0}
              onClick={() => openFilterMenu(menu.key)}
            />
            <DropdownMenu
              isOpen={activeFilterMenu === menu.key}
              onClose={() => openFilterMenu(menu.key)}
            >
              {menu.content}
              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="flex-1 rounded-xl bg-cyan-600 px-3 py-2 text-xs font-bold text-white hover:bg-cyan-700 transition-colors"
                >
                  {t('apply', 'Uygula')}
                </button>
              </div>
            </DropdownMenu>
          </div>
        ))}

        {/* Sıfırla butonu */}
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <RotateCcw size={13} />
          {t('reset', 'Sıfırla')}
        </button>
      </div>

      {/* Export butonu */}
      {showExport && (
        <div className="ml-auto relative">
          <DropdownButton
            label={t('attendance_export', 'Dışa Aktar')}
            icon={FileSpreadsheet}
            isActive={activeFilterMenu === 'export'}
            onClick={() => openFilterMenu('export')}
          />
          <DropdownMenu
            isOpen={activeFilterMenu === 'export'}
            onClose={() => openFilterMenu('export')}
            alignRight
          >
            <div className="space-y-1 p-1">
              {[
                { format: 'csv', label: 'CSV (.csv)' },
                { format: 'excel', label: 'Excel (.xlsx)' },
              ].map((opt) => (
                <button
                  key={opt.format}
                  type="button"
                  onClick={() => { onExport?.(opt.format); openFilterMenu('export'); }}
                  className="w-full text-left rounded-xl px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  📥 {opt.label}
                </button>
              ))}
            </div>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

export default AttendanceFilterBar;